
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnalysisProvider, useAnalysis } from "@/contexts/AnalysisContext";
import { GlobalAnalysisSheet } from "@/components/diffs/GlobalAnalysisSheet";
// import { AnalysisFloatingButton } from "@/components/diffs/AnalysisFloatingButton"; // Removed - redundant with Reports tab

import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import Events from "./pages/Events";
import Companies from "./pages/Companies";
import CompaniesV2 from "./pages/CompaniesV2";
import News from "./pages/News";
import Diffs from "./pages/Diffs";
import Diffs2 from "./pages/Diffs2";
import Diffs2Detail from "./pages/Diffs2Detail";
import Icons from "./pages/Icons";
import DiffDetail from "./pages/DiffDetail";
import DiffEditorV3 from "./pages/DiffEditorV3";
import DiffReport from "./pages/DiffReport";
import EventDetail from "./pages/EventDetail";
import ClaudeTasks from "./pages/ClaudeTasks";
import ClaudeTaskDetail from "./pages/ClaudeTaskDetail";
import Collections from "./pages/Collections";
import CollectionDetail from "./pages/CollectionDetail";
import AssetDetailPage from "./pages/AssetDetailPage";
import CanvaReturn from "./pages/CanvaReturn";
import OAuthRedirect from "./pages/OAuthRedirect";
import Authorize from "./pages/Authorize";
import Test from "./pages/Test";
import Admin from "./pages/Admin";
import AspectWeaveKitDemo from "./pages/AspectWeaveKitDemo";
import AssetViewerTest from "./pages/AssetViewerTest";
import { AssetEditor } from "./pages/AssetEditor";

import Dashboard from "./pages/Dashboard";
import DashboardScrapes from "./pages/DashboardScrapes";
import DashboardScrapeDetail from "./pages/DashboardScrapeDetail";
import DashboardExtractions from "./pages/DashboardExtractions";
import Jobs from "./pages/Jobs";
import DigestAdmin from "./pages/DigestAdmin";
import DigestDetail from "./pages/DigestDetail";
import Requests from "./pages/Requests";
import Reports from "./pages/Reports";
import ReportsTest from "./pages/ReportsTest";
import ReportsTags from "./pages/ReportsTags";
import Search from "./pages/Search";
import Links from "./pages/Links";
import UnauthorizedAccess from "./pages/UnauthorizedAccess";
import CompanyDetailsPage from "./pages/CompanyDetailsPage";
// import EditCompanyPage from "./pages/EditCompanyPage"; // OLD - Replaced with PricingSaasCompanyPage
import PricingSaasCompanyPage from "./pages/PricingSaasCompanyPage";
import CompanyPlansPage from "./pages/PricingSaasCompanyPage/CompanyPlansPage";
import { NavigationContent } from "./components/navigation/NavigationContent";
import { ProfileMenu } from "./components/navigation/ProfileMenu";
import { useAppSelector } from './hooks/useAppSelector';
import { useAppDispatch } from './hooks/useAppDispatch';
import { fetchQuarterlyEvents } from './utils/quarterlyEventsHelper';
import { fetchCompanies } from "./utils/companiesHelper";
import { checkAdminAuthorization } from "./utils/authHelpers";
import { warmupManagementApi } from "./utils/externalDiffsAPI";
import { getAdminStatusCache, setAdminStatusCache, clearAdminStatusCache } from "./utils/adminCache";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable automatic retries and delays
      retry: false,
      // Execute queries immediately, don't wait
      networkMode: 'always',
      // Refetch on mount to ensure fresh data
      refetchOnMount: true,
      // Don't wait for window focus
      refetchOnWindowFocus: false,
      // Stale time of 5 minutes (data is considered fresh for this duration)
      staleTime: 5 * 60 * 1000,
    },
  },
});

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isOpen: isAnalysisOpen } = useAnalysis();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminChecked, setIsAdminChecked] = useState(false);
  const [adminCacheChecked, setAdminCacheChecked] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  const companies = useAppSelector(state => state.supaCompanies.companies);
  const companiesLoading = useAppSelector(state => state.supaCompanies.loading);
  // Remove eager events loading - events will be loaded lazily when needed
  // Removed scrapbook/swipefile loading - no longer used


  useEffect(() => {
    const pageLoadStartTime = performance.now();
    console.log(`🚀 [App] Page load starting for ${location.pathname} at ${new Date().toISOString()}`);

    // Define page types for conditional loading
    const isMetricsPage = false; // Metrics is now part of Admin page
    const isCompaniesPage = location.pathname === '/companies' || location.pathname === '/' || location.pathname === '/companies-old';
    const isCompanyDetailsPage = location.pathname.startsWith('/companies/');
    const isDiffsPage = location.pathname.startsWith('/diffs');
    const isCollectionsPage = location.pathname.startsWith('/collections');
    const isNewsPage = location.pathname.startsWith('/news');
    const isAdminPage = location.pathname.startsWith('/admin');
    const isReportsPage = location.pathname.startsWith('/reports');

    // Pre-warm the management API edge function for diffs routes
    if (isDiffsPage) {
      warmupManagementApi();
    }

    // Determine which data to load based on the current route
    const dataToLoad = [];

    console.log(`📊 [App] Page type detection (${(performance.now() - pageLoadStartTime).toFixed(0)}ms):`, {
      isReportsPage,
      isCompaniesPage,
      isCompanyDetailsPage,
      isDiffsPage,
      isCollectionsPage,
      isNewsPage,
      isAdminPage
    });

    // Load quarterly events only if not on companies page or company details page (optimization)
    if (!isCompaniesPage && !isCompanyDetailsPage) {
      dataToLoad.push(
        fetchQuarterlyEvents().catch(error => {
          console.error('❌ [App] Error loading quarterly events:', error);
        })
      );
    } else {
      //console.log('⚡ [App] Skipping quarterly events load for companies/details page (optimization)');
    }

    // Only load companies data for pages that actually need it
    // Skip companies loading for diffs, collections, news, admin, reports pages
    const pagesWithoutCompaniesData = isMetricsPage || isCompanyDetailsPage || isDiffsPage || isCollectionsPage || isNewsPage || isAdminPage || isReportsPage;

    if (!pagesWithoutCompaniesData) {
      dataToLoad.push(
        fetchCompanies().catch(error => {
          console.error('❌ [App] Error loading companies:', error);
        })
      );
    } else {
      console.log('⚡ [App] Skipping companies load for current page (optimization)');
    }

    // Events are now loaded lazily when needed - removed eager loading

    // Execute all required data loading operations
    if (dataToLoad.length > 0) {
      Promise.all(dataToLoad).then(() => {
        const totalLoadTime = performance.now() - pageLoadStartTime;
        console.log(`✅ [App] All data loaded in ${totalLoadTime.toFixed(0)}ms for ${location.pathname}`);
      });
    } else {
      const skipTime = performance.now() - pageLoadStartTime;
      console.log(`⏭️ [App] No data to load for ${location.pathname} (took ${skipTime.toFixed(0)}ms to determine)`);
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session && !['/authorize', '/test', '/oauth/redirect'].includes(location.pathname)) {
        console.log('❌ [App Auth] No session, redirecting to /authorize');
        navigate('/authorize'); // Use React Router navigate instead of `window.location.href`
        return;
      } else if (!session) {
        console.log('✅ [App Auth] No session but path is allowed:', location.pathname);
      }

      // If user is logged in, check admin status with cache optimization
      if (session && !['/authorize', '/test', '/oauth/redirect'].includes(location.pathname)) {
        try {
          // First check cache for faster loading
          const cachedAdminStatus = getAdminStatusCache(session.user.id);
          if (cachedAdminStatus !== null) {
            console.log('🚀 [App] Using cached admin status:', cachedAdminStatus);
            setIsAdmin(cachedAdminStatus);
            setIsAdminChecked(true);
            setAdminCacheChecked(true);
            
            // Still verify in background for security, but don't block UI
            checkAdminAuthorization(navigate, (actualIsAdmin) => {
              // Update cache if status changed
              if (actualIsAdmin !== cachedAdminStatus) {
                console.log('🔄 [App] Admin status changed, updating cache');
                setIsAdmin(actualIsAdmin);
                setAdminStatusCache(actualIsAdmin, session.user.id);
              }
            }).catch(error => {
              console.error('❌ [App] Background admin check failed:', error);
            });
          } else {
            // No cache, do full check
            const isAdminUser = await checkAdminAuthorization(navigate, (isAdmin) => {
              setIsAdmin(isAdmin);
            });
            setIsAdmin(isAdminUser);
            setIsAdminChecked(true);
            
            // Cache the result
            setAdminStatusCache(isAdminUser, session.user.id);
          }
        } catch (error) {
          console.error('❌ [App] Error checking admin status:', error);
          setIsAdmin(false);
          clearAdminStatusCache();
        } finally {
          if (!adminCacheChecked) {
            setIsAdminChecked(true);
          }
        }
      }

      setIsAuthChecking(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      
      // Only handle logout events, not temporary session issues during OAuth flows
      if (event === 'SIGNED_OUT' && !['/authorize', '/test', '/oauth/redirect'].includes(location.pathname)) {
        console.log('🔓 [App Auth] User signed out, redirecting to /authorize');
        // Clear admin cache on logout
        clearAdminStatusCache();
        window.location.href = '/authorize';
      } else if (event === 'SIGNED_IN' && session) {
        console.log('🔐 [App Auth] User signed in, checking admin status');
        // Re-check admin status when auth state changes and update cache
        checkAdminAuthorization(navigate, (isAdmin) => {
          setIsAdmin(isAdmin);
          setIsAdminChecked(true);
          setAdminStatusCache(isAdmin, session.user.id);
        });
      } else if (!session && event !== 'SIGNED_OUT') {
        console.log('⚠️ [App Auth] Session lost but not signed out event - ignoring to prevent OAuth interference');
      }
    });

    return () => subscription.unsubscribe();
  }, [location.pathname, navigate]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleClearPrompt = () => {
    setAiPrompt('');
  };

  // Skip companies loading check if we're on pages that don't need companies data
  const isMetricsPage = location.pathname === '/metrics';
  const isCompaniesPage = location.pathname === '/companies' || location.pathname === '/' || location.pathname === '/companies-old';
  const isCompanyDetailsPage = location.pathname.startsWith('/companies/');
  const isEditPage = location.pathname.startsWith('/edit/');
  const isDiffsPage = location.pathname.startsWith('/diffs');
  const isCollectionsPage = location.pathname.startsWith('/collections');
  const isNewsPage = location.pathname.startsWith('/news');
  const isAdminPage = location.pathname.startsWith('/admin');
  const pagesWithoutCompaniesData = isMetricsPage || isCompanyDetailsPage || isDiffsPage || isCollectionsPage || isNewsPage || isAdminPage;

  // Optimize loading indicator logic based on current route
  const shouldShowLoading = isAuthChecking ||
                           isLoading ||
                           (!pagesWithoutCompaniesData && !isEditPage && companiesLoading);

  if (shouldShowLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">
            {!pagesWithoutCompaniesData && !isEditPage && companiesLoading
              ? "Loading basic companies data..."
              : "Loading application..."}
          </p>
        </div>
      </div>
    );
  }

  // If user is logged in but not an admin, show unauthorized access page
  // Allow edit pages for admin users only
  if (isAdminChecked && !isAdmin && location.pathname !== '/authorize' && location.pathname !== '/test' && !isEditPage) {
    return <UnauthorizedAccess />;
  }
  
  // If user is not admin but trying to access edit page, redirect to unauthorized
  if (isAdminChecked && !isAdmin && isEditPage) {
    return <UnauthorizedAccess />;
  }

  const showBackButton = false;

  if (location.pathname === '/test' || location.pathname === '/Test') {
    return (
      <Routes>
        <Route path="/test" element={<Test />} />
      </Routes>
    );
  }

  if (location.pathname === '/authorize') {
    return (
      <Routes>
        <Route path="/authorize" element={<Authorize />} />
      </Routes>
    );
  }

  if (location.pathname === '/oauth/redirect') {
    return (
      <Routes>
        <Route path="/oauth/redirect" element={<OAuthRedirect />} />
      </Routes>
    );
  }

  return (
    <div className={`transition-all duration-300 ${isAnalysisOpen ? 'mr-[40rem]' : ''}`}>
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              {showBackButton && (
                <Button variant="ghost" asChild>
                  <Link to="/" className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Explorer
                  </Link>
                </Button>
              )}
              <NavigationContent onClearPrompt={handleClearPrompt} />
            </div>
            <div className="flex items-center gap-4">
              <ProfileMenu />
            </div>
          </div>
        </div>
      </nav>
      <Routes>
        <Route path="/" element={<CompaniesV2 />} />
        <Route path="/events" element={<Events />} />
        <Route path="/news" element={<News />} />
        <Route path="/diffs" element={<Diffs />} />
        <Route path="/diffs2/:slug/:period" element={<Diffs2Detail />} />
        <Route path="/diffs2" element={<Diffs2 />} />
        <Route path="/icons" element={<Icons />} />
        <Route path="/diffs/reports/:reportId" element={<DiffReport />} />
        <Route path="/diffs/events/:id" element={<EventDetail />} />
        <Route path="/diffs/:id/edit" element={<DiffEditorV3 />} />
        <Route path="/diffs/:id" element={<DiffDetail />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard/scrapes" element={<DashboardScrapes />} />
        <Route path="/dashboard/scrapes/:slug" element={<DashboardScrapeDetail />} />
        <Route path="/dashboard/extractions" element={<DashboardExtractions />} />
        <Route path="/tasks" element={<ClaudeTasks />} />
        <Route path="/tasks/:id" element={<ClaudeTaskDetail />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/:id" element={<Reports />} />
        <Route path="/reports/test" element={<ReportsTest />} />
        <Route path="/reports/tags" element={<ReportsTags />} />
        <Route path="/search" element={<Search />} />
        <Route path="/links" element={<Links />} />
        <Route path="/collections" element={<Collections />} />
        <Route path="/collections/:id" element={<CollectionDetail />} />
        <Route path="/collections/:collectionId/:assetId" element={<AssetDetailPage />} />
        <Route path="/canva/return" element={<CanvaReturn />} />
        <Route path="/canva-return" element={<CanvaReturn />} />
        <Route path="/companies" element={<CompaniesV2 />} />
        <Route path="/companies-old" element={<Companies />} /> {/* Legacy companies page */}
        <Route path="/companies/:slug" element={<CompanyDetailsPage />} />
        {/* <Route path="/edit/:slug" element={<EditCompanyPage />} /> */} {/* OLD - Replaced with PricingSaasCompanyPage */}
        <Route path="/edit/:slug" element={<PricingSaasCompanyPage />} />
        <Route path="/edit/:slug/plans" element={<CompanyPlansPage />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/assets/:assetId/edit" element={<AssetEditor />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/digests" element={<DigestAdmin />} />
        <Route path="/digests/:id" element={<DigestDetail />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/aspect-weave-kit-demo" element={<AspectWeaveKitDemo />} />
        <Route path="/asset-viewer-test" element={<AssetViewerTest />} />
        <Route path="/oauth/redirect" element={<OAuthRedirect />} />
        <Route path="/authorize" element={<Authorize />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AnalysisProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppContent />
            <GlobalAnalysisSheet />
            {/* <AnalysisFloatingButton /> - Removed: redundant with Reports tab */}
          </BrowserRouter>
        </AnalysisProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
